import React, { useCallback, useMemo } from 'react';
import { Box, CardTypesEnum, Checkbox, Inscription, Option, RawButton, SelectDropdown } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { EMPTY_FIELD_VALUE, SHORT_DATE_FORMAT } from '@/common/constants';
import ROUTES from '@/common/routes';
import { RequiredLabel } from '@/components/elements/RequiredLabel';
import { ReactivationPlan } from '@/modules/employer/components/EmployerReactivation/types';
import PlanTypeIcon from '@/modules/plan/PlanSetup/Sidebar/PlanTypeIcon';
import colors from '@/styles/colors';

import { useEmployerReactivationPlanField } from './hooks/useEmployerReactivationPlanField';

const MAX_ALLOWED_LENGTH_SYMBOL_TIP = 53;
const REQUIRED_TEXT = 'Required';

interface Props {
  testId: string;
  plan: ReactivationPlan;
  options: Option[];
  hasFieldErrors?: boolean;
}

export const EmployerReactivationPlanField: React.FC<Props> = ({
  testId,
  plan,
  options,
  hasFieldErrors,
}) => {
  const { checked, bankAccountValue, handleCheckboxChange, handleBankAccountChange } = useEmployerReactivationPlanField(plan.id, plan?.isPlanLevelFunding);
  
  const handleSeeDetails = useCallback(() => {
    window.open(ROUTES.PLAN_BY_ID(plan.id), '_blank');
  }, [plan.id]);
  
  const hasError = useMemo(
    () => hasFieldErrors && checked && plan?.isPlanLevelFunding && !bankAccountValue,
    [hasFieldErrors, checked, plan.isPlanLevelFunding, bankAccountValue]
  );
  const labelText = useMemo(() => {
    switch (plan?.type) {
      case CardTypesEnum.HSA:
        return 'Health Savings Account';
      case CardTypesEnum.HCFSA:
        return 'Healthcare FSA (Limited Use)';
      case CardTypesEnum.DCAP:
        return 'Dependent Care FSA';
      case CardTypesEnum.HRA:
        return 'Health Reimbursement Arrangement(Limited Use)';
      case CardTypesEnum.TRANSIT:
        return 'Commuter - Transit';
      case CardTypesEnum.PARKING:
        return 'Commuter - Parking';
      default:
        return '';
      }
  }, [plan?.type]);
  
  const dates = useMemo(() => {
    if (!plan?.validFrom && !plan?.validTo) {
      return EMPTY_FIELD_VALUE;
    }
    const { validFrom, validTo } = plan;
    const formattedValidFrom = validFrom ? dayjs(validFrom).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    const formattedValidTo = validTo ? dayjs(validTo).format(SHORT_DATE_FORMAT) : EMPTY_FIELD_VALUE;
    return `${formattedValidFrom} - ${formattedValidTo}`;
  }, [plan]);

  return (
    <Box direction="row" justify="between" data-testid={testId}>
      <Box width="244px">
        <Box margin={{ bottom: 'spacing8' }}>
          <PlanTypeIcon
            type={plan?.type}
            data-testid={`${testId}_plan-type_icon_${plan?.type}`}
            color={colors.cardTypes[plan?.type]}
            size='24px'
          />
        </Box>

        <Box gap="spacing2">
          <Inscription
            weight={700}
            color="textBody"
            wordBreak="break-word"
            data-testid={`${testId}_${plan.name || labelText}_name`}
            lineHeight="20px"
            title={(plan.name || labelText)?.length > MAX_ALLOWED_LENGTH_SYMBOL_TIP ? plan.name || labelText : ''} 
          >
            {plan.name || labelText}
          </Inscription>
          <Box direction="row">
            <Inscription lineHeight="20px">{dates}</Inscription>
          </Box>

          <RawButton onClick={handleSeeDetails} onAuxClick={handleSeeDetails}>
            <Inscription color="textAccent" lineHeight="20px" weight="normal">
              See Details
            </Inscription>
          </RawButton>
        </Box>
      </Box>

      <Box width="320px">
        <Checkbox
          testId={testId}
          checked={checked}
          onChange={handleCheckboxChange}
        />

        {checked && plan.isPlanLevelFunding && (
          <Box margin={{ top: 'spacing16' }} data-testid={`${testId}_bank-account-section`}>
            <Inscription margin={{ bottom: 'spacing8' }}>
              {`${plan.name || labelText} funds at the plan level. Select the bank account to be used.`}
            </Inscription>
            <Box margin={{ bottom: 'spacing8' }}>
              <RequiredLabel title="Bank account" isBold isRequired testId={`${testId}_label`}/>
            </Box>
            <SelectDropdown
              id={testId}
              testId={`${testId}_dropdown`}
              name={testId}
              placeholder="Select bank account"
              singleMode
              options={options}
              value={bankAccountValue}
              onChange={handleBankAccountChange}
              errorText={hasError ? REQUIRED_TEXT : undefined}
            />

            <Box direction="row" margin={{ top: 'spacing8', bottom: 'spacing2'}}>
              <Inscription weight="bold" margin={{ right: 'spacing4' }}>
                Method:
              </Inscription>

              <Inscription>
                Pull funds
              </Inscription>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
